/**
 * Fasi dell'avvio, condivise da tutte le schermate di attesa.
 *
 * @remarks
 * Prima esistevano quattro maschere diverse (splash HTML, PersistGate, Suspense,
 * verifica worker), ognuna con la sua grafica: l'utente vedeva quattro stacchi
 * per un solo avvio. Ora la maschera e' una sola e cambia solo la fase, cosi'
 * il passaggio da un gate all'altro non si vede.
 */
export type BootPhase = 'starting' | 'session' | 'database' | 'ready';

interface BootPhaseDescriptor {
  /** Posizione nella sequenza: serve a impedire che la barra torni indietro. */
  readonly order: number;
  /** Percentuale di riempimento della barra. */
  readonly progress: number;
  /** Riga di testo mostrata sotto la barra. */
  readonly label: string;
}

export const BOOT_PHASES: Record<BootPhase, BootPhaseDescriptor> = {
  starting: { order: 0, progress: 18, label: 'Avvio dell\u2019app' },
  session: { order: 1, progress: 45, label: 'Ripristino della sessione' },
  database: { order: 2, progress: 74, label: 'Preparazione dati offline' },
  ready: { order: 3, progress: 100, label: 'Tutto pronto' },
};

/**
 * Fase piu' avanzata raggiunta in questo caricamento di pagina.
 *
 * @remarks
 * I gate si montano e smontano in sequenza e ognuno dichiara la propria fase.
 * Senza questa memoria, passando da un gate all'altro la barra potrebbe
 * arretrare: un avanzamento che torna indietro e' peggio di nessun avanzamento.
 * Vive a livello di modulo perche' il suo ciclo di vita e' quello della pagina,
 * non quello di un componente React.
 */
let highestOrderReached = -1;

/** Registra la fase dichiarata da un gate e restituisce quella da mostrare. */
export function trackBootPhase(phase: BootPhase): BootPhase {
  const { order } = BOOT_PHASES[phase];
  if (order > highestOrderReached) {
    highestOrderReached = order;
    return phase;
  }

  const current = (Object.keys(BOOT_PHASES) as BootPhase[]).find(
    (key) => BOOT_PHASES[key].order === highestOrderReached,
  );
  return current ?? phase;
}

/** Solo per i test: azzera la memoria di modulo. */
export function resetBootPhaseTracking(): void {
  highestOrderReached = -1;
}
